import { Alert, Spin } from "antd";
import { Annotations, Shape } from "plotly.js";
import Plot, { Figure } from "react-plotly.js";
import NoData from "../../NewSite/Components/OptimizationConfigurationForm/NoData";
import { plotlyDateAxis } from "../../../util/formatDate";

export type TimeSeries = {
  timestamps: Date[];
  values: (number | null)[];
  name: string;
  color?: string;
};

type VerticalLine = {
  timestamp: Date;
  label?: string;
  color?: string;
};

type HorizontalLine = {
  value: number;
  label?: string;
  color?: string;
};

export type TimeSeriesChartProps = {
  data?: TimeSeries[];
  title: string;
  className?: string;
  isLoading?: boolean;
  error?: Error | null;
  line?: "steps" | "smooth";
  color?: string;
  yAxisTitle?: string;
  yAxisTickSuffix?: string;
  extra?: {
    verticalLines?: VerticalLine[];
    horizontalLines?: HorizontalLine[];
  };
};

export const TimeSeriesChart: React.FC<TimeSeriesChartProps> = (props) => {
  if (props.error) {
    return (
      <Alert message="Error" description={props.error.message} type="error" />
    );
  }

  if (props.isLoading || !props.data) {
    return (
      <div className="w-full flex justify-center items-center min-h-96">
        <Spin />
      </div>
    );
  }

  const lineToShape: Record<
    string,
    "linear" | "spline" | "hv" | "vh" | "hvh" | "vhv"
  > = {
    steps: "hv",
    smooth: "spline",
  };

  const mappedData: Figure["data"] = props.data?.map((series, index) => {
    return {
      x: series.timestamps.map((ts) => ts.getTime()),
      y: series.values,
      type: "scatter",
      mode: "lines",
      fill: index === 0 ? "tozeroy" : "tonexty", // First series fills from zero, others stack on top
      line: {
        shape: lineToShape[props.line ?? "steps"],
        color: series.color,
      },
      name: series.name,
      stackgroup: "stacked_area",
      showlegend: true,
    };
  });

  const verticalLines: VerticalLine[] = props.extra?.verticalLines ?? [];

  const shapes: Partial<Shape>[] = [];
  const annotations: Partial<Annotations>[] = [];

  const sortedVerticalLines = [...verticalLines].sort(
    (a, b) => a.timestamp.getTime() - b.timestamp.getTime(),
  );
  sortedVerticalLines.forEach((vLine, index) => {
    const labelY = 1.04 + (index % 2) * 0.09;
    shapes.push({
      type: "line" as const,
      x0: vLine.timestamp.getTime(),
      x1: vLine.timestamp.getTime(),
      y0: 0,
      y1: labelY,
      xref: "x" as const,
      yref: "paper" as const,
      line: {
        color: vLine.color ?? "#1e8c6f",
        width: 4,
        dash: "solid" as const,
      },
    });
    annotations.push({
      x: vLine.timestamp.getTime(),
      y: labelY,
      xref: "x",
      yref: "paper" as const,
      text: vLine.label,
      showarrow: false,
      xanchor: "center" as const,
      yanchor: "bottom" as const,
      font: {
        color: vLine.color ?? "#1e8c6f",
        size: 12,
        weight: 1000,
      },
    });
  });

  for (const hLine of props.extra?.horizontalLines ?? []) {
    shapes.push({
      type: "line" as const,
      x0: 0,
      x1: 1,
      y0: hLine.value,
      y1: hLine.value,
      xref: "paper" as const,
      yref: "y" as const,
      line: {
        color: hLine.color ?? "#d97706",
        width: 2,
        dash: "dash" as const,
      },
    });
    if (hLine.label) {
      annotations.push({
        x: 1,
        y: hLine.value,
        xref: "paper" as const,
        yref: "y" as const,
        text: hLine.label,
        showarrow: false,
        xanchor: "right" as const,
        yanchor: "bottom" as const,
        font: {
          color: hLine.color ?? "#d97706",
          size: 12,
          weight: 1000,
        },
      });
    }
  }

  const layout: Figure["layout"] = {
    title: props.title,
    xaxis: {
      ...plotlyDateAxis,
      title: "Time",
      autorange: true, // Automatically center data
      rangemode: "tozero", // Ensures a proper fit
    },
    yaxis: {
      title: props.yAxisTitle ?? "Value",
      ticksuffix: props.yAxisTickSuffix,
    },
    legend: {
      orientation: "h",
    },
    shapes,
    annotations,
  };

  return mappedData.length > 0 ? (
    <Plot data={mappedData} layout={layout} className={props.className} />
  ) : (
    <NoData />
  );
};
